import { BACKEND_RECOVERY_POLL_MS } from '@australiangreens/ag-internal-components';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/** How often Redis recovery refetches active non-preview queries (shared package cadence). */
export const REDIS_RECOVERY_POLL_MS = BACKEND_RECOVERY_POLL_MS;

const SEARCH_PREVIEW_ROOT_KEYS = new Set(['advsearch', 'preview']);

/** Previews stay off while any backend/dependency outage is latched. */
export function areSearchPreviewsPaused({
  backendStatus,
}: {
  backendStatus: '' | 'UP' | 'DOWN';
}): boolean {
  return backendStatus === 'DOWN';
}

/**
 * Advanced-search and quick-filter previews are huge. Refetching them on the
 * Redis recovery interval hangs the tab, so they are excluded from that poll.
 */
export function isSearchPreviewQueryKey(queryKey: QueryKey): boolean {
  const root = queryKey[0];
  return typeof root === 'string' && SEARCH_PREVIEW_ROOT_KEYS.has(root);
}

export function isRedisRecoveryInvalidateTarget(query: { queryKey: QueryKey }): boolean {
  return !isSearchPreviewQueryKey(query.queryKey);
}

/** Drop in-flight search previews when a Redis outage is latched. */
export function cancelSearchPreviews(queryClient: QueryClient) {
  void queryClient.cancelQueries({ queryKey: ['advsearch'] });
  void queryClient.cancelQueries({ queryKey: ['preview'] });
}

/** Refetch active queries except search previews, so a success can clear Redis. */
export function invalidateQueriesForRedisRecovery(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: isRedisRecoveryInvalidateTarget,
  });
}

/**
 * Starts the Redis recovery poll. Cancels in-flight previews immediately and
 * invalidates other active queries on an interval. Returns a cleanup function.
 */
export function startRedisRecoveryPolling(queryClient: QueryClient): () => void {
  cancelSearchPreviews(queryClient);
  const intervalId = window.setInterval(() => {
    invalidateQueriesForRedisRecovery(queryClient);
  }, REDIS_RECOVERY_POLL_MS);
  return () => window.clearInterval(intervalId);
}
